/**
 * PATH: packages/ui/src/kanban/components/lane/inbox-filters/InboxLaneFilterStrip.tsx
 * FILE_ID: UI-KANBAN-INBOX-LANE-FILTER-STRIP-001
 * PURPOSE: Compact curated filter chip strip under the primary inbox lane header.
 * WHY: Mimic Email Hub filter axes without embedding Hub sidebar or importing email-hub.
 * MAIN EXPORTS: InboxLaneFilterStrip
 * NON-RESPONSIBILITIES: Persisting state / applying filters (parent hook owns that).
 */
import { ExternalLink, FilterX, Loader2, Search } from 'lucide-react'

import type { InboxLaneFilterState } from './model'
import { INBOX_LANE_CATEGORY_OPTIONS, inboxLaneFiltersActive } from './model'

export interface InboxLaneFilterStripProps {
  value: InboxLaneFilterState
  onChange: (next: InboxLaneFilterState) => void
  onClear: () => void
  hubUrl?: string | null
  categoryLoading?: boolean
  categoryError?: string | null
}

export function InboxLaneFilterStrip({
  value,
  onChange,
  onClear,
  hubUrl,
  categoryLoading,
  categoryError,
}: InboxLaneFilterStripProps) {
  const active = inboxLaneFiltersActive(value)
  const patch = (partial: Partial<InboxLaneFilterState>) =>
    onChange({ ...value, ...partial })

  return (
    <div
      data-testid="kanban-inbox-lane-filters"
      className="flex flex-col gap-1 border-b border-neutral-200/70 bg-emerald-50/40 px-2 py-1.5 dark:border-zinc-700/60 dark:bg-emerald-950/20"
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <label className="relative min-w-[8rem] flex-1">
          <Search
            className="pointer-events-none absolute left-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-neutral-400"
            aria-hidden
          />
          <input
            type="search"
            data-testid="kanban-inbox-lane-filter-search"
            value={value.search}
            onChange={(e) => patch({ search: e.target.value })}
            placeholder="Search lane…"
            className="h-6 w-full rounded-md border border-neutral-200 bg-white pl-6 pr-2 text-[11px] text-neutral-800 placeholder:text-neutral-400 focus:border-emerald-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
          />
        </label>

        <select
          data-testid="kanban-inbox-lane-filter-read"
          aria-label="Read state"
          value={value.isRead}
          onChange={(e) =>
            patch({ isRead: e.target.value as InboxLaneFilterState['isRead'] })
          }
          className="h-6 rounded-md border border-neutral-200 bg-white px-1.5 text-[11px] text-neutral-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
        >
          <option value="all">All read states</option>
          <option value="unread">Unread</option>
          <option value="read">Read</option>
        </select>

        <select
          data-testid="kanban-inbox-lane-filter-allocation"
          aria-label="Job allocation"
          value={value.allocation}
          onChange={(e) =>
            patch({
              allocation: e.target.value as InboxLaneFilterState['allocation'],
            })
          }
          className="h-6 rounded-md border border-neutral-200 bg-white px-1.5 text-[11px] text-neutral-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
        >
          <option value="all">All allocation</option>
          <option value="unallocated">Unallocated</option>
          <option value="allocated">Allocated</option>
        </select>

        <select
          data-testid="kanban-inbox-lane-filter-category"
          aria-label="Category"
          value={value.category}
          onChange={(e) => patch({ category: e.target.value })}
          className="h-6 max-w-[9rem] rounded-md border border-neutral-200 bg-white px-1.5 text-[11px] text-neutral-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
        >
          {INBOX_LANE_CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.id || 'all'} value={opt.id}>
              {opt.label}
            </option>
          ))}
        </select>

        <input
          type="text"
          data-testid="kanban-inbox-lane-filter-mailbox"
          aria-label="Mailbox"
          value={value.mailbox}
          onChange={(e) => patch({ mailbox: e.target.value })}
          placeholder="Mailbox / account id"
          className="h-6 w-[8.5rem] rounded-md border border-neutral-200 bg-white px-1.5 text-[11px] text-neutral-800 placeholder:text-neutral-400 focus:border-emerald-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
        />

        {categoryLoading && (
          <Loader2
            className="h-3.5 w-3.5 animate-spin text-emerald-600"
            aria-label="Loading category matches"
          />
        )}

        {active && (
          <button
            type="button"
            data-testid="kanban-inbox-lane-filter-clear"
            onClick={onClear}
            className="inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[10px] font-medium text-neutral-600 hover:bg-white/80 dark:text-zinc-300 dark:hover:bg-zinc-800"
            title="Clear lane filters"
          >
            <FilterX className="h-3 w-3" aria-hidden />
            Clear
          </button>
        )}

        {hubUrl && (
          <a
            href={hubUrl}
            data-testid="kanban-inbox-lane-filter-hub"
            className="inline-flex h-6 items-center gap-1 rounded-md px-1.5 text-[10px] font-medium text-emerald-700 hover:bg-white/80 dark:text-emerald-300 dark:hover:bg-zinc-800"
            title="Open the same filter subset in Email Hub"
          >
            <ExternalLink className="h-3 w-3" aria-hidden />
            Email Hub
          </a>
        )}
      </div>

      {categoryError && (
        <p
          data-testid="kanban-inbox-lane-filter-category-error"
          className="text-[10px] text-amber-700 dark:text-amber-300"
        >
          {categoryError}
        </p>
      )}
    </div>
  )
}
